import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Skeleton,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Stack,
  useTheme,
  IconButton,
  Paper,
} from '@mui/material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import PeopleIcon from '@mui/icons-material/People';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BackupIcon from '@mui/icons-material/Backup';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import TableViewIcon from '@mui/icons-material/TableView';
import { formatDate } from '../utils/dateUtils';

import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { createApiClient } from '../services/apiClient';
import { APP_VERSION } from '../config';

const actionIcon = (action) => {
  if (action === 'created') return <PersonAddIcon fontSize="small" sx={{ color: 'success.main' }} />;
  if (action === 'deleted') return <DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />;
  return <EditIcon fontSize="small" sx={{ color: 'warning.main' }} />;
};

const actionColor = (action) => {
  if (action === 'created') return 'success';
  if (action === 'deleted') return 'error';
  return 'warning';
};

const GlassCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      background: (theme) => 
        theme.palette.mode === 'dark' 
          ? 'rgba(30, 30, 30, 0.6)' 
          : 'rgba(255, 255, 255, 0.7)',
      backdropFilter: 'blur(10px)',
      border: '1px solid',
      borderColor: (theme) => 
        theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.05)' 
          : 'rgba(0, 0, 0, 0.05)',
      borderRadius: 4,
      ...sx,
    }}
  >
    {children}
  </Card>
);

const StatCard = ({ label, value, icon, color, loading, subtitle }) => (
  <GlassCard sx={{ transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '16px',
          bgcolor: (theme) => theme.palette[color].main + '15',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: `${color}.main`,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        {loading ? (
          <Skeleton width={60} height={40} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </CardContent>
  </GlassCard>
);

const DashboardPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const { notifications, loading: notifLoading } = useNotifications();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) return;
    const client = createApiClient(token);
    client
      .get('/system/stats')
      .then(({ data }) => setStats(data))
      .catch((err) => console.error('[Dashboard] Failed to load stats:', err))
      .finally(() => setLoading(false));
  }, []);

  const recentActivity = useMemo(() => notifications.slice(0, 8), [notifications]);
  const firstName = user?.name?.split(' ')[0] || user?.username || 'Doctor';

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/patients?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header & Search */}
      <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, background: 'linear-gradient(45deg, #1976d2, #64b5f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {t('Welcome')}, {firstName}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
            {t('Management Overview')} • {formatDate(new Date())}
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSearchSubmit} sx={{ minWidth: { xs: '100%', sm: 350 } }}>
          <TextField
            fullWidth
            placeholder={t('Search patient...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            size="medium"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
              sx: { 
                borderRadius: 4, 
                bgcolor: 'background.paper',
                '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                '&:hover fieldset': { borderColor: 'primary.main' },
              }
            }}
          />
        </Box>
      </Box>

      {/* Statistics Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label={t('Total Patients')}
            value={stats?.totals?.patients ?? '—'}
            icon={<PeopleIcon size={24} />}
            color="primary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label={t('Rectal Cancer')}
            value={stats?.totals?.specialty ?? '—'}
            icon={<LocalHospitalIcon size={24} />}
            color="error"
            loading={loading}
            subtitle={t('Ca ΟΡΘΟΥ Cases')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label={t("Today's Activity")}
            value={(stats?.today?.visits || 0) + (stats?.today?.operations || 0)}
            icon={<MedicalServicesIcon size={24} />}
            color="secondary"
            loading={loading}
            subtitle={`${stats?.today?.visits || 0} ${t('Visits')}, ${stats?.today?.operations || 0} ${t('Ops')}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label={t('Recent Files')}
            value={stats?.today?.files ?? '—'}
            icon={<AttachFileIcon size={24} />}
            color="success"
            loading={loading}
            subtitle={t('Last 7 Days')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Left Column: Analytics & Shortcuts */}
        <Grid item xs={12} lg={8}>
          <Stack spacing={3}>
            {/* Clinical Volume Chart */}
            <GlassCard sx={{ p: 3 }}>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('Clinical Volume Trend')}</Typography>
                <Chip label={t('Last 12 Months')} size="small" variant="outlined" />
              </Box>
              <Box sx={{ height: 300, width: '100%' }}>
                {loading ? (
                  <Skeleton variant="rectangular" height="100%" sx={{ borderRadius: 2 }} />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats?.volume || []}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                      <XAxis 
                        dataKey="month" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                        dy={10}
                      />
                      <YAxis 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          borderRadius: 12, 
                          border: 'none', 
                          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                          background: theme.palette.background.paper 
                        }} 
                      />
                      <Legend verticalAlign="top" height={36}/>
                      <Bar dataKey="visits" name={t('Visits')} fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="operations" name={t('Operations')} fill={theme.palette.secondary.main} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </GlassCard>

            <Grid container spacing={3}>
              {/* Diagnosis Distribution */}
              <Grid item xs={12} md={6}>
                <GlassCard sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{t('Top Diagnoses')}</Typography>
                  <Box sx={{ height: 400 }}>
                    {loading ? (
                      <Skeleton variant="circular" height="100%" width={240} sx={{ mx: 'auto' }} />
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={stats?.diagnoses || []}
                            cx="50%"
                            cy="40%"
                            innerRadius={70}
                            outerRadius={95}
                            paddingAngle={5}
                            dataKey="count"
                            nameKey="label"
                          >
                            {(stats?.diagnoses || []).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={[
                                theme.palette.primary.main,
                                theme.palette.secondary.main,
                                theme.palette.success.main,
                                theme.palette.warning.main,
                                theme.palette.info.main,
                                theme.palette.error.main
                              ][index % 6]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend 
                            layout="vertical"
                            verticalAlign="bottom" 
                            align="center"
                            iconType="circle"
                            formatter={(value, entry) => (
                              <span style={{ color: theme.palette.text.primary, fontSize: '11px', fontWeight: 500 }}>
                                {value} ({entry.payload.count})
                              </span>
                            )}
                            wrapperStyle={{ 
                              paddingTop: '20px'
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                </GlassCard>
              </Grid>

              {/* Specialty Segment (Ca ΟΡΘΟΥ) */}
              <Grid item xs={12} md={6}>
                <GlassCard sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <LocalHospitalIcon color="error" />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {t('New Rectal Cancer Cases')}
                    </Typography>
                    <Chip label={t('Last 3 Months')} size="small" variant="outlined" sx={{ ml: 'auto' }} />
                  </Box>
                  <Stack spacing={1.5}>
                    {loading ? (
                      [1, 2, 3].map(i => <Skeleton key={i} height={40} />)
                    ) : (stats?.recentSpecialtyPatients || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        {t('No new cases in the last 3 months')}
                      </Typography>
                    ) : (stats?.recentSpecialtyPatients || []).map((p) => (
                      <Paper
                        key={`patient-${p.id}`}
                        elevation={0}
                        onClick={() => navigate(`/patients/${p.id}`)}
                        sx={{
                          p: 1, px: 1.5, borderRadius: 2, bgcolor: 'action.hover', cursor: 'pointer',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          '&:hover': { bgcolor: 'action.selected' }
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{p.patientName}</Typography>
                          <Typography variant="caption" color="text.secondary">{formatDate(p.date)}</Typography>
                        </Box>
                        <Chip label={t('New')} size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                      </Paper>
                    ))}
                  </Stack>
                </GlassCard>
              </Grid>
            </Grid>

            {/* Quick Actions */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, ml: 1 }}>{t('Quick Actions')}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate('/patients?action=new-patient')}
                    sx={{ height: 100, borderRadius: 4, flexDirection: 'column', gap: 1, textTransform: 'none' }}
                  >
                    <PersonAddIcon />
                    {t('New Patient')}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/query?tab=batch')}
                    color="primary"
                    sx={{ height: 100, borderRadius: 4, flexDirection: 'column', gap: 1, textTransform: 'none', borderWidth: 2, bgcolor: 'primary.light', color: 'primary.dark' }}
                  >
                    <TableViewIcon />
                    {t('Batch Diagnosis Lookup')}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/settings?tab=database&action=backup')}
                    sx={{ height: 100, borderRadius: 4, flexDirection: 'column', gap: 1, textTransform: 'none', borderWidth: 2, color: 'info.main', borderColor: 'info.main' }}
                  >
                    <BackupIcon />
                    {t('System Backup')}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/settings?tab=migration&action=export')}
                    color="inherit"
                    sx={{ height: 100, borderRadius: 4, flexDirection: 'column', gap: 1, textTransform: 'none', borderWidth: 2, borderColor: 'divider' }}
                  >
                    <FileDownloadIcon />
                    {t('Export All Data')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Grid>

        {/* Right Column: Activity Feed */}
        <Grid item xs={12} lg={4}>
          <GlassCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('Recent Activity')}</Typography>
                <IconButton size="small"><NotificationsActiveIcon fontSize="small" /></IconButton>
              </Box>
              
              {notifLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
              ) : recentActivity.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8, opacity: 0.5 }}>
                  <Typography variant="body2">{t('No recent clinical changes')}</Typography>
                </Box>
              ) : (
                <Stack spacing={0.5}>
                  {recentActivity.map((n, idx) => (
                    <Paper
                      key={n.id}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        bgcolor: 'transparent',
                        borderBottom: idx < recentActivity.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        transition: 'background 0.2s',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ mt: 0.5 }}>{actionIcon(n.action)}</Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.3 }}>
                            {n.message}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {new Date(n.created_at).toLocaleString('el-GR', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                            <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                            <Typography variant="caption" sx={{ color: `${actionColor(n.action)}.main`, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                              {n.action}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              )}
              
              <Button 
                fullWidth 
                variant="text" 
                size="small" 
                sx={{ mt: 2, textTransform: 'none', fontWeight: 600 }}
                onClick={() => navigate('/patients')}
              >
                {t('View all patients')}
              </Button>
            </CardContent>
          </GlassCard>
        </Grid>
      </Grid>

      {/* Homepage Version Footer */}
      <Box sx={{ mt: 5, pb: 1, textAlign: 'center', opacity: 0.7 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          MySurgeon EMR • v{APP_VERSION} • {t('Clinical Suite')}
        </Typography>
      </Box>
    </Box>
  );
};

export default DashboardPage;
